/* @flow */

import { compose } from 'ramda';
import { connect } from 'react-redux';
import { getEventId } from '@e180/domain/lib/event/util';
import { isMembershipFavoriteTutorialCompleted } from '@e180/domain/lib/membership/util';
import { getTopicFavoriteURL } from '@e180/domain/lib/topic/util';
import * as Sentry from '@sentry/browser';

import TopicFavoriteButton from './TopicFavoriteButton';
import { isErrorReportingEnabled } from 'src/shared/app/base/selector/appEnvSelectors';
import { completeFavoriteTutorial } from 'src/shared/app/market/action/marketActions';
import { showFavoriteNotification } from 'src/shared/app/market/action/uiMarketActions';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';
import { isHTTP4xxError } from 'src/shared/core/util/httpUtils';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import { bookmarkTopic } from 'src/shared/domain/topic/action/topicActions';

import type { Topic } from '@e180/domain/lib/topic/type';
import type { State } from 'src/shared/core/type/reduxTypes';

type Props = {
  topic: Topic,
  source: string,
  large?: boolean,
  lightTheme?: boolean,
};

const mapStateToProps = (
  state: State,
  { topic, large, lightTheme }: Props,
) => ({
  topic,
  large,
  lightTheme,
  // Used in `mergeProps` only
  membership: getMembership(state),
  event: getEventSafely(state),
  isErrorReportingEnabled: isErrorReportingEnabled(state),
});

const mapDispatchToProps = (dispatch: any): Object => ({ dispatch });

const mergeProps = (
  stateProps: Object,
  { dispatch }: Object,
  { source }: Object,
): Object => {
  const { isErrorReportingEnabled } = stateProps;

  return {
    ...stateProps,
    handleClick(bookmarked: boolean, topicId: number) {
      const { topic, event, membership } = stateProps;

      if (bookmarked) {
        trackGAEvent(source, 'Mark topic as favorite', topicId);

        if (membership && !isMembershipFavoriteTutorialCompleted(membership)) {
          dispatch(showFavoriteNotification());
          dispatch(completeFavoriteTutorial(getEventId(event)));
        }
      } else {
        trackGAEvent(source, 'Unmark topic as favorite', topicId);
      }

      dispatch(bookmarkTopic(getTopicFavoriteURL(topic), bookmarked)).catch(
        (e) => {
          if (isErrorReportingEnabled && !isHTTP4xxError(e)) {
            Sentry.captureException(e);
          } else {
            // TODO: handle error in UI
          }
        },
      );
    },
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
)(TopicFavoriteButton);
